import React, { useMemo, useState } from 'react'
import { Container, Row, Col, Button, Popover, PopoverBody } from 'reactstrap'
import { DateRange } from 'react-date-range'
import moment from 'moment'
import Select from 'react-select'

import ClaimsTab from './tabs/ClaimsTab'
import IncidentsTab from './tabs/IncidentsTab'
import UsersTab from './tabs/UsersTab'
import useActivityReport from './useActivityReport'
import { ACTION_OPTIONS, filterRows, hasUpdateActions } from './activityReport'
import './IncidentReports.css'

const TABS = [
    { id: 'incidents', label: 'Incidents', icon: 'fa fa-car' },
    { id: 'claims', label: 'Claims', icon: 'fa fa-gavel' },
    { id: 'users', label: 'Users', icon: 'fa fa-users' },
]

const formatDateLabel = (date) => moment(date).format('MMM D, YYYY')

const getDefaultRange = () => [
    { startDate: moment().startOf('week').toDate(), endDate: moment().endOf('week').toDate(), key: 'selection' },
]

const IncidentReports = () => {
    const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
    const [dateRangeSelection, setDateRangeSelection] = useState(getDefaultRange())
    const [activeTab, setActiveTab] = useState('incidents')
    const [activeView, setActiveView] = useState('chart')
    const [filters, setFilters] = useState({ users: [], branches: [], groups: [] })
    // Empty means every action -- `filterRows` treats an empty list as "no filter".
    const [actionFilter, setActionFilter] = useState([])

    const startDate = moment(dateRangeSelection[0].startDate).format('YYYY-MM-DD')
    const endDate = moment(dateRangeSelection[0].endDate).format('YYYY-MM-DD')

    // Only the user/branch/group filters go to the hook -- the action filter
    // belongs to the Users tab alone, and applying it globally would empty the
    // other tabs.
    //
    // A group expands to its members' ids, so several groups union together and
    // then intersect with whatever else is selected.
    const rowFilters = useMemo(
        () => ({
            users: filters.users.map((item) => item.value),
            branches: filters.branches.map((item) => item.value),
            userIds: [...new Set(filters.groups.flatMap((group) => group.userIds || []))],
        }),
        [filters]
    )

    const { loading, error, filteredRows, userOptions, branchOptions, groupOptions } = useActivityReport({
        startDate,
        endDate,
        filters: rowFilters,
    })

    const actionFilterValues = useMemo(() => actionFilter.map((option) => option.value), [actionFilter])

    const userTabRows = useMemo(
        () => filterRows(filteredRows, { actions: actionFilterValues }),
        [filteredRows, actionFilterValues]
    )

    return (
        <Container fluid className="incident-reports-container">
            <Row className="incident-reports-header">
                <Col xs="12" md="6" className="incident-reports-title">
                    <h4>Incident Reports</h4>
                    <p>Activity breakdown by user, branch and record type</p>
                </Col>
                <Col xs="12" md="6" className="incident-reports-filter">
                    <Button
                        id="incidentReportsDateRange"
                        className="incident-reports-date-button"
                        onClick={() => setIsDateRangeOpen(!isDateRangeOpen)}
                    >
                        <i className="fa fa-calendar" aria-hidden="true"></i>
                        {formatDateLabel(dateRangeSelection[0].startDate)} - {formatDateLabel(dateRangeSelection[0].endDate)}
                    </Button>
                    <Popover
                        trigger="legacy"
                        placement="bottom"
                        isOpen={isDateRangeOpen}
                        target="incidentReportsDateRange"
                        toggle={() => setIsDateRangeOpen(!isDateRangeOpen)}
                    >
                        <PopoverBody className="incident-reports-popover">
                            <DateRange
                                ranges={dateRangeSelection}
                                onChange={(ranges) => setDateRangeSelection([ranges.selection])}
                                editableDateInputs={true}
                                moveRangeOnFirstSelection={false}
                            />
                        </PopoverBody>
                    </Popover>
                </Col>
            </Row>

            <div className="viz-tabs" role="tablist">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={activeTab === tab.id ? 'viz-tab is-active' : 'viz-tab'}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        <i className={tab.icon} aria-hidden="true"></i>
                        {tab.label}
                    </button>
                ))}
            </div>

            <Row>
                <Col xs="12" className="incident-reports-card">
                    <div className="incident-reports-panel-header">
                        <div className="incident-reports-view-toggle">
                            <button
                                type="button"
                                className={activeView === 'chart' ? 'active' : ''}
                                onClick={() => setActiveView('chart')}
                            >
                                <i className="fa fa-bar-chart" aria-hidden="true"></i>
                                Chart View
                            </button>
                            <button
                                type="button"
                                className={activeView === 'table' ? 'active' : ''}
                                onClick={() => setActiveView('table')}
                            >
                                <i className="fa fa-table" aria-hidden="true"></i>
                                Table View
                            </button>
                        </div>
                        <div className="incident-reports-filters">
                            {/* Scopes the Users tab only, so it appears with the other
                                filters rather than inside a chart card, and only when
                                that tab is showing. */}
                            {activeTab === 'users' && (
                                <div className="incident-reports-select incident-reports-select--wide">
                                    <Select
                                        classNamePrefix="incident-reports-select"
                                        placeholder="All actions"
                                        value={actionFilter}
                                        onChange={(value) => setActionFilter(value || [])}
                                        options={ACTION_OPTIONS}
                                        isMulti
                                        isClearable
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                            )}
                            <div className="incident-reports-select">
                                <Select
                                    classNamePrefix="incident-reports-select"
                                    placeholder="All groups"
                                    value={filters.groups}
                                    onChange={(value) => setFilters((prev) => ({ ...prev, groups: value || [] }))}
                                    options={groupOptions}
                                    isMulti
                                    isClearable
                                />
                            </div>
                            <div className="incident-reports-select">
                                <Select
                                    classNamePrefix="incident-reports-select"
                                    placeholder="All users"
                                    value={filters.users}
                                    onChange={(value) => setFilters((prev) => ({ ...prev, users: value || [] }))}
                                    options={userOptions}
                                    isMulti
                                    isClearable
                                />
                            </div>
                            <div className="incident-reports-select">
                                <Select
                                    classNamePrefix="incident-reports-select"
                                    placeholder="All branches"
                                    value={filters.branches}
                                    onChange={(value) => setFilters((prev) => ({ ...prev, branches: value || [] }))}
                                    options={branchOptions}
                                    isMulti
                                    isClearable
                                />
                            </div>
                        </div>
                    </div>

                    {loading && <div className="incident-reports-status">Loading report...</div>}
                    {!loading && error && (
                        <div className="incident-reports-status error">Unable to load report. Please try again.</div>
                    )}
                    {!loading && !error && activeTab === 'incidents' && (
                        <IncidentsTab
                            rows={filteredRows}
                            activeView={activeView}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    )}
                    {!loading && !error && activeTab === 'claims' && (
                        <ClaimsTab
                            rows={filteredRows}
                            activeView={activeView}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    )}
                    {!loading && !error && activeTab === 'users' && (
                        <UsersTab
                            rows={userTabRows}
                            updateActionsSelected={hasUpdateActions(actionFilterValues)}
                            startDate={startDate}
                            endDate={endDate}
                            activeView={activeView}
                        />
                    )}
                </Col>
            </Row>
        </Container>
    )
}

export default IncidentReports
